<script setup lang="ts">
import { ManualTaskWorkItemDto } from '@/api/generator';
import FormLabel from '@/components/FormLabel/FormLabel.vue';
import Attachments from '@/components/forms/Attachments/Attachments.vue';
import Textarea from '@/components/forms/Textarea/Textarea.vue';
import { useFormMode } from '@/components/forms/useFormMode';
import NamedGroup from '@/components/NamedGroup/NamedGroup.vue';
import RTSystemTile from '@/components/RTSystemTile/RTSystemTile.vue';
import { toRef } from 'vue';
import { useManualTask } from '../utils/useManualTask';
import { useJobOccurrence } from '../utils/useJobOccurrence';
import JobExecutionWorkSteps from './JobExecutionWorkSteps.vue';
import ManualWorkItemPanel from './workItems/manual/ManualWorkItemPanel.vue';

const props = defineProps<{
  workitem: ManualTaskWorkItemDto;
}>();

const mode = useFormMode();

const { jobOccurrence, areAllWorkstepsSkipped, isSkippAllPossible, toggleSkipAllWorksteps } =
  useJobOccurrence();

const { supportSystemGroups, numGroups } = useManualTask(toRef(props, 'workitem'));
</script>

<template>
  <v-row
    v-if="
      numGroups ||
      workitem.instruction?.textAreaContent ||
      workitem.instruction?.attachments?.length
    "
  >
    <v-col v-if="numGroups" cols="8">
      <FormLabel class="mb-2" :label="$t('DosimetryAndQaEquipment')">
        <NamedGroup :groups="supportSystemGroups">
          <template #default="group">
            <v-row density="compact" class="mt-2">
              <v-col
                v-for="supportSystem in group.items"
                :key="supportSystem.id"
                cols="12"
                lg="6"
                xxl="4"
                class="mb-4"
              >
                <RTSystemTile class="w-100 h-100" :item="supportSystem" />
              </v-col>
            </v-row>
          </template>
        </NamedGroup>
      </FormLabel>
    </v-col>

    <v-col
      v-if="workitem.instruction?.textAreaContent || workitem.instruction?.attachments?.length"
      cols="4"
    >
      <FormLabel :label="$t('Instruction_one')">
        <Textarea
          v-if="workitem.instruction?.textAreaContent"
          :modelValue="workitem.instruction.textAreaContent"
          mode="details"
        />
        <v-divider
          v-if="workitem.instruction?.textAreaContent && workitem.instruction?.attachments?.length"
          color="primary-lighten-4"
          class="mt-1 mb-1"
          thickness="1"
        ></v-divider>
        <Attachments
          v-if="workitem.instruction?.attachments?.length"
          :modelValue="workitem.instruction?.attachments"
          mode="details"
        />
      </FormLabel>
    </v-col>
  </v-row>

  <v-row
    v-if="
      mode === 'execution' && (workitem.workSteps?.length ?? 0) > 1 && isSkippAllPossible(workitem)
    "
  >
    <v-col class="d-flex justify-end">
      <v-checkbox
        :label="$t('JobExecutionSkipAll')"
        density="compact"
        hideDetails
        class="mr-2"
        :modelValue="areAllWorkstepsSkipped(workitem)"
        @click.stop="toggleSkipAllWorksteps(workitem, !areAllWorkstepsSkipped(workitem))"
      />
    </v-col>
  </v-row>

  <v-row v-if="jobOccurrence?.id">
    <v-col>
      <JobExecutionWorkSteps
        class="mt-4"
        :workSteps="workitem.workSteps || undefined"
        initialExpand="All"
      >
        <template #workitem="{ step }">
          <ManualWorkItemPanel :jobOccurrenceId="jobOccurrence.id" :step="step" />
        </template>
      </JobExecutionWorkSteps>
    </v-col>
  </v-row>
</template>
